import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  GrCostBreakdownUpsertFailure,
  GrCostBreakdownUpsertInput,
  UpsertGrCostBreakdownMutation,
  UpsertGrCostBreakdownMutationVariables,
} from "@/graphql/codegen/graphql"
import { UpsertGrCostBreakdownDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpsertGrCostBreakdownReturn {
  upsertGrCostBreakdown: (input: GrCostBreakdownUpsertInput) => Promise<boolean>
  isUpserting: boolean
}

export const useUpsertGrCostBreakdown = (): UseUpsertGrCostBreakdownReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    UpsertGrCostBreakdownMutation,
    UpsertGrCostBreakdownMutationVariables
  >(UpsertGrCostBreakdownDocument)

  const upsertGrCostBreakdown = useCallback(
    async (input: GrCostBreakdownUpsertInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.grCostBreakdown.upsert
      const hasError = error || output?.__typename === "GrCostBreakdownUpsertFailure"

      if (hasError) {
        const message = error?.message ?? (output as GrCostBreakdownUpsertFailure).error.message
        notify({ type: NotificationType.Error, message })

        return false
      }

      notify({ type: NotificationType.Success, message: "Cost breakdown saved" })

      return true
    },
    [executeMutation, notify]
  )

  return { upsertGrCostBreakdown, isUpserting: fetching }
}
